// ===== CONFIGURACIÓN DE API =====

// Detectar si estamos en desarrollo o producción
const isDevelopment = process.env.NODE_ENV === 'development';

// ---------------------------------------------------------------------------
// Autenticación por cookie httpOnly (B2)
// ---------------------------------------------------------------------------
// El access token vive en una cookie httpOnly `accessToken` que JavaScript no
// puede leer: así un XSS no lo puede robar. El cliente nunca maneja el token;
// solo guarda en `localStorage` el marcador no sensible `userRoles` (y el
// `username` para la UI) que escribe el login. El refresco es reactivo: un 401
// fuerza `/refresh`, que reemite la cookie.

const REFRESH_LOCK = 'botomatic-jwt-refresh';

// ---------------------------------------------------------------------------
// Configuración de la API
// ---------------------------------------------------------------------------
const API_CONFIG = {
  BASE_URL: isDevelopment
    ? 'http://localhost:8080/api'
    : `${window.location.protocol}//${window.location.host}/api`,

  AUTH: {
    SIGNUP: '/auth/public/signup',
    SIGNIN: '/auth/public/signin',
    REFRESH: '/auth/public/refresh',
    LOGOUT: '/auth/logout',
    PASSWORD: '/auth/password',
  },

  TELEGRAM: {
    CHATS: '/telegram/chats',
    SEND_MESSAGE: (chatId) => `/telegram/chats/${chatId}/send`
  },

  INSTAGRAM: {
    CHATS: '/instagram/chats',
    SEND_MESSAGE: (chatId) => `/instagram/chats/${chatId}/messages`,
    COMMENTS_PERMISSIONS: (botId) => `/instagram/comments/permissions?botId=${botId}`,
    COMMENTS_ACCESS: '/instagram/comments/access',
    MEDIA_LIST: (botId, page = 0, size = 20) =>
      `/instagram/media?botId=${botId}&page=${page}&size=${size}`,
    COMMENTS_LIST: (botId, mediaId, page = 0, size = 50) =>
      `/instagram/media/${encodeURIComponent(mediaId)}/comments?botId=${botId}&page=${page}&size=${size}`,
    REPLY_COMMENT: (commentId) => `/instagram/comments/${encodeURIComponent(commentId)}/reply`,
    HIDE_COMMENT: (commentId) => `/instagram/comments/${encodeURIComponent(commentId)}/hide`,
    DELETE_COMMENT: (commentId, botId) =>
      `/instagram/comments/${encodeURIComponent(commentId)}?botId=${botId}`,
    AUTO_DM_LIST: (botId) => `/instagram/auto-dm?botId=${botId}`,
    AUTO_DM_CREATE: '/instagram/auto-dm',
    AUTO_DM_UPDATE: (ruleId) => `/instagram/auto-dm/${ruleId}`,
    AUTO_DM_DELETE: (ruleId, botId) => `/instagram/auto-dm/${ruleId}?botId=${botId}`
  },

  BOTS: {
    // Permisos por funcionalidad del usuario actual. Un solo GET del que salen las
    // pestañas: canales, comentarios, plantillas, registros, atención, y qué bots
    // tienen un número de WhatsApp conectado.
    FEATURES: '/bots/features',
  },

  WEBSOCKET: {
    URL: isDevelopment
      ? 'http://localhost:8080/ws'
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`
  }
};

export const getApiUrl = (endpoint) => `${API_CONFIG.BASE_URL}${endpoint}`;

// ---------------------------------------------------------------------------
// Refresh reactivo con single-flight
// ---------------------------------------------------------------------------
// Si varias llamadas reciben un 401 a la vez, todas comparten la misma promesa
// de `/refresh`, para no lanzar varios refrescos concurrentes desde la pestaña.
// Entre pestañas, `navigator.locks` serializa el refresco.

let refreshInFlight = null;

/**
 * Llama a `/refresh`. El token nuevo viaja en la cookie httpOnly que reemite el
 * backend y el cuerpo ya no lo trae; aquí solo se actualiza el marcador
 * `userRoles`. Devuelve `true` si el refresco fue bien y `null` si falla
 * (incluido un corte de red).
 */
const doRefreshFetch = async () => {
  try {
    const response = await fetch(getApiUrl('/auth/public/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (data && Array.isArray(data.roles)) {
      localStorage.setItem('userRoles', JSON.stringify(data.roles));
    }
    return true;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
};

const runRefresh = () => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
      if (locks && typeof locks.request === 'function') {
        return await locks.request(REFRESH_LOCK, doRefreshFetch);
      }
      return await doRefreshFetch();
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};

// ---------------------------------------------------------------------------
// Sesión
// ---------------------------------------------------------------------------

const clearSession = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('userRoles');
  localStorage.removeItem('username');
};

export const clearClientSession = clearSession;

const redirectToLogin = () => {
  if (typeof window !== 'undefined' && window.location) {
    window.location.href = '/';
  }
};

/**
 * Indica si hay una sesión iniciada, sin leer el access token. El token vive en
 * una cookie httpOnly que JavaScript no puede leer; lo que queda en
 * `localStorage` es solo el marcador no sensible `userRoles` que escribe el
 * login. Sirve para que el router decida de forma optimista si pintar la zona
 * protegida; la validación real la hace el backend en la primera petición, que
 * devuelve 401 y expulsa al login si la sesión ya no vale.
 */
export const haySesionActiva = () => {
  try {
    return localStorage.getItem('userRoles') !== null;
  } catch (_) {
    return false;
  }
};

/**
 * Fuerza un refresco de la cookie de acceso. Comparte la promesa single-flight
 * con el resto del sistema. Pensado para el WebSocket, que ante un error de
 * autenticación reemite la cookie y reconecta. Devuelve `true` si el refresco
 * fue bien, o `null` si falló.
 */
export const forceRefresh = () => runRefresh();

// ---------------------------------------------------------------------------
// apiRequest: wrapper de fetch sobre la cookie httpOnly, con refresh reactivo
// ---------------------------------------------------------------------------
/**
 * Las peticiones se autentican con la cookie httpOnly `accessToken` (va sola
 * gracias a `credentials: 'include'`), no con un header `Authorization`: así el
 * token nunca pasa por JavaScript y un XSS no lo puede leer.
 *
 * Refresco reactivo: si llega un 401 (cookie caducada u otra causa), se fuerza
 * un `/refresh` —que reemite la cookie— y se reintenta una vez. Si el refresh
 * falla (p. ej. 401/400 porque también caducó el refresh, o un corte de
 * red), se limpia la sesión y se redirige al login.
 */
export const apiRequest = async (url, options = {}) => {
  // Con FormData el navegador fija el Content-Type con el boundary; ponerlo a mano lo rompe.
  const esFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const enviar = () => fetch(url, {
    ...options,
    headers: {
      ...(esFormData ? {} : { 'Content-Type': 'application/json' }),
      'X-Requested-With': 'XMLHttpRequest',
      ...options.headers,
    },
    credentials: 'include',
  });

  let response = await enviar();

  if (response.status === 401) {
    const renovado = await runRefresh();
    if (renovado) {
      response = await enviar();
    } else {
      clearSession();
      redirectToLogin();
    }
  }

  return response;
};

export default API_CONFIG;
